import { Box, CircularProgress } from '@material-ui/core';
import { getTicketsByLoanId, Ticketing } from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Font } from '@/components/shared/components/Font';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { ActivitySummary } from '@/components/widgets/CustomerSupport/components/CustomerSupportDialog/components/CustomerSupportHeader/components/ActivitySummary';
import { CustomerSupportHeader } from '@/components/widgets/CustomerSupport/components/CustomerSupportDialog/components/CustomerSupportHeader/CustomerSupportHeader';
import { CustomerSupportLog } from '@/components/widgets/CustomerSupport/components/CustomerSupportDialog/components/CustomerSupportLog/CustomerSupportLog';
import { TicketSelector } from '@/components/widgets/CustomerSupport/components/CustomerSupportDialog/components/TicketSelector/TicketSelector';
import { PendingDataType } from '@/config/types';
import { useCache } from '@/hooks/requests/useCache';

interface CustomerSupportDialogProps {
  loanId: string;
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
}

export const CustomerSupportDialog = ({ loanId, opened, setOpened }: CustomerSupportDialogProps) => {
  const { data: tickets, loading } = useCache(getTicketsByLoanId, { loanId });

  const [selectedTicketing, setSelectedTicketing] = useState<Ticketing | undefined>(undefined);

  useEffect(() => {
    if (tickets) setSelectedTicketing(tickets.ticketing[0]);
  }, [tickets]);

  const handleClose = () => {
    setOpened(false);
  };

  return (
    <NemuruDialog
      avoidDefaultClose
      opened={opened}
      setOpened={setOpened}
      buttons={
        <>
          <StandardButton disabled={loading} onClick={handleClose}>
            <Text tId={'CLOSE'} />
          </StandardButton>
        </>
      }
      dialogProps={{ maxWidth: 'md' }}
    >
      <Box mb={4}>
        <Font variant={'h2'} color={'title'}>
          <Text strong tId={'CUSTOMER_SUPPORT'} />
        </Font>
      </Box>
      {!tickets || tickets.id !== loanId ? (
        <Box
          pt={3}
          display={'flex'}
          flexDirection={'column'}
          width={'100%'}
          alignItems={'center'}
          minHeight={'120px'}
          justifyContent={'center'}
        >
          <CircularProgress color={'secondary'} size={36} />
          <Font variant={'subtitle2'} color={'textSecondary'} style={{ paddingTop: '12px' }}>
            <Text tId={'LOADING_TICKETS'} />
          </Font>
        </Box>
      ) : (
        <Box className={'appear-anim'} display={'flex'} flexDirection={'column'}>
          {tickets.ticketing.length === 0 || !selectedTicketing ? (
            <PendingDataWarning type={PendingDataType.CUSTOMER_SUPPORT} />
          ) : (
            <>
              <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
                <CustomerSupportHeader ticketing={selectedTicketing} />
                <TicketSelector
                  loanId={loanId}
                  tickets={tickets}
                  setSelectedTicketing={setSelectedTicketing}
                  selectedTicketing={selectedTicketing}
                />
              </Box>
              <ActivitySummary loanId={loanId} ticketing={selectedTicketing} />
              <CustomerSupportLog ticketing={selectedTicketing} />
            </>
          )}
        </Box>
      )}
    </NemuruDialog>
  );
};
